import React, { useEffect, useState } from 'react';
import useAuthStore from '../../lib/auth-store';
import CreateUserWizard from '../../components/team/CreateUserWizard';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Users, Plus, Search, MoreHorizontal, UserPlus, Pause, XCircle,
  MailPlus, RefreshCw, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const statusColors = {
  active: 'bg-green-100 text-green-700 border-green-200',
  invited: 'bg-blue-100 text-blue-700 border-blue-200',
  suspended: 'bg-red-100 text-red-700 border-red-200',
  deactivated: 'bg-gray-100 text-gray-600 border-gray-200',
};

const roleLabels = { iso: 'ISO', iso_agent: 'ISO Agent', iso_subagent: 'ISO Subagent' };

export default function ISOUserManagement() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', role: 'iso_agent', password: '' });
  const [saving, setSaving] = useState(false);
  const [tempPwd, setTempPwd] = useState('');
  const [tempPwdOpen, setTempPwdOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  useEffect(() => {
    let list = users;
    if (roleFilter !== 'all') list = list.filter((u) => u.role === roleFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((u) =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [users, search, roleFilter]);

  const loadUsers = async () => {
    try {
      const data = await api.getTenantUsers();
      setUsers(data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.email.trim() || !form.first_name.trim()) { toast.error('Name and email required'); return; }
    setSaving(true);
    try {
      if (dialogMode === 'invite') {
        await api.inviteTenantUser({
          first_name: form.first_name, last_name: form.last_name,
          email: form.email, role: form.role,
        });
        toast.success('Invitation sent');
      } else {
        const result = await api.createTenantUser({
          first_name: form.first_name, last_name: form.last_name,
          email: form.email, phone: form.phone, role: form.role,
          password: form.password || undefined,
        });
        if (result.temp_password) {
          setTempPwd(result.temp_password);
          setTempPwdOpen(true);
        }
        toast.success('User created');
      }
      setDialogOpen(false);
      loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="iso-user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Team Management</h1>
          <p className="text-sm text-muted-foreground mt-1">{users.length} users in your organization</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={loadUsers}><RefreshCw className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => { setDialogMode('invite'); setForm({ first_name: '', last_name: '', email: '', role: 'iso_agent' }); setDialogOpen(true); }} data-testid="iso-invite-user-btn">
            <MailPlus className="h-4 w-4 mr-2" /> Invite
          </Button>
          <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={() => setWizardOpen(true)} data-testid="iso-create-user-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Create User
          </Button>
        </div>
      </div>

      <CreateUserWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={() => { loadUsers(); }}
      />

      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search team members..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="iso-search-users" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {Object.entries(roleLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="iso-users-table">
            <thead>
              <tr className="border-b text-left text-sm text-muted-foreground">
                <th className="px-6 py-3 font-medium">User</th>
                <th className="px-6 py-3 font-medium">Role</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Last Login</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">No users found</td></tr>}
              {filtered.map((u) => {
                const initials = `${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase() || 'U';
                const name = u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.email;
                return (
                  <tr key={u.id} className="border-b last:border-0 hover:bg-accent/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center text-xs font-semibold text-primary">{initials}</div>
                        <div>
                          <p className="font-medium text-sm">{name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4"><span className="text-xs font-medium px-2 py-1 bg-accent rounded">{roleLabels[u.role] || u.role}</span></td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[u.status] || 'bg-gray-100'}`}>{u.status}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}</td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {['iso_agent', 'iso_subagent'].includes(u.role) && (
                            <DropdownMenuItem onClick={() => { window.location.href = `/team/${u.id}`; }} data-testid={`team-economics-${u.id}`}>
                              <Users className="h-4 w-4 mr-2" /> Team Economics
                            </DropdownMenuItem>
                          )}
                          {u.status === 'active' && <DropdownMenuItem className="text-red-600"><Pause className="h-4 w-4 mr-2" /> Suspend</DropdownMenuItem>}
                          {u.status === 'invited' && <DropdownMenuItem><MailPlus className="h-4 w-4 mr-2" /> Resend Invite</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Invite Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{dialogMode === 'invite' ? 'Invite Team Member' : 'Create Team Member'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>First Name *</Label><Input value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} className="mt-1" data-testid="iso-user-form-first" /></div>
              <div><Label>Last Name</Label><Input value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} className="mt-1" /></div>
            </div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className="mt-1" data-testid="iso-user-form-email" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setField('role', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="iso_agent">ISO Agent</SelectItem>
                  <SelectItem value="iso_subagent">ISO Subagent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {dialogMode === 'create' && (
              <>
                <div><Label>Phone</Label><Input value={form.phone || ''} onChange={(e) => setField('phone', e.target.value)} className="mt-1" /></div>
                <div><Label>Password (blank = auto-generated)</Label><Input type="password" value={form.password || ''} onChange={(e) => setField('password', e.target.value)} className="mt-1" /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={handleSave} disabled={saving} data-testid="iso-save-user-btn">
              {saving ? 'Saving...' : dialogMode === 'invite' ? 'Send Invite' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp Password Dialog */}
      <Dialog open={tempPwdOpen} onOpenChange={setTempPwdOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Temporary Password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Share this securely. User must change it on first login.</p>
          <div className="flex items-center gap-2 mt-2">
            <Input value={tempPwd} readOnly className="font-mono" />
            <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(tempPwd); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <DialogFooter><Button onClick={() => setTempPwdOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
